import React, { useState } from 'react';
import { Upload, CheckCircle, Loader } from 'lucide-react';
import '../pages/AdminPages.css';

const BulkCreate = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [queueStatus, setQueueStatus] = useState(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    setFile(selected);
    setError('');

    // Parse CSV/Excel preview
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        const lines = text?.split('\n').slice(0, 6) || [];
        setPreview(lines.map((line, i) => ({
          id: i,
          data: line.split(',').slice(0, 5)
        })));
      } catch (err) {
        setError('Failed to preview file');
      }
    };
    reader.readAsText(selected);
  };

  const handleUploadAndQueue = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/work-orders/bulk/upload-and-queue', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setQueueStatus(data);
      setSuccess(`✓ Successfully queued ${data.totalQueued} work orders for processing`);
      setFile(null);
      setPreview([]);
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>📦 Bulk Create Work Orders</h1>
      </div>

      {error && <div className="status-message error">{error}</div>}
      {success && <div className="status-message success">{success}</div>}

      <div className="form-section">
        <h3>Upload & Queue Batch</h3>

        {/* File Upload Area */}
        <div className="file-upload-area" style={{
          border: '2px dashed var(--primary-blue)',
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: 'var(--gray-50)',
          cursor: 'pointer',
          transition: 'all 0.15s'
        }}>
          <label style={{ cursor: 'pointer', display: 'block' }}>
            <Upload size={48} style={{ marginBottom: '1rem', color: 'var(--primary-blue)' }} />
            <h4>Choose CSV or Excel file</h4>
            <p>Upload batch work order data</p>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        {file && (
          <div style={{ marginTop: '1.5rem' }}>
            <div className="status-message success" style={{ marginBottom: '1.5rem' }}>
              ✓ File selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </div>

            {preview.length > 0 && (
              <div className="preview-table">
                <h4>Preview (first 5 rows)</h4>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer</th>
                      <th>Report Type</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, idx) => (
                      <tr key={row.id}>
                        <td>{idx + 1}</td>
                        <td>{row.data[0]}</td>
                        <td>{row.data[1]}</td>
                        <td><span className="badge-success">pending</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="actions" style={{ marginTop: '1.5rem' }}>
              <button className="btn btn-secondary" onClick={() => { setFile(null); setPreview([]); }}>
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleUploadAndQueue}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Upload & Queue
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {queueStatus && (
        <div className="form-section">
          <h3>✓ Queue Status</h3>
          <div className="status-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem'
          }}>
            <div className="stat-card">
              <div className="stat-value">{queueStatus.totalQueued}</div>
              <div className="stat-label">Queued for Processing</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{queueStatus.processing}</div>
              <div className="stat-label">Currently Processing</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{queueStatus.completed}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{queueStatus.failed}</div>
              <div className="stat-label">Failed</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkCreate;
